import StarBorderIcon from '@mui/icons-material/StarBorder'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import DialogContentText from '@mui/material/DialogContentText'
import Rating from '@mui/material/Rating'
import Snackbar from '@mui/material/Snackbar'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useMutation } from '@tanstack/react-query'
import KNDialog from 'components/KNDialog/KNDialog'
import KNDialogFormErrors from 'components/KNDialog/KNDialogFormErrors'
import KNFormButtonGroup from 'components/KNForm/KNFormButtonGroup'
import KNFormText from 'components/KNForm/KNFormText'
import { CompanyType } from 'context/authentication/Company.types'
import { UserContext } from 'context/authentication/UserContext'
import { analyticsEvent } from 'global/helpers/analytics'
import { processDefaultValues, processServerErrorMessages } from 'global/helpers/form'
import { regexEmail } from 'global/helpers/validators'
import i18n from 'i18n'
import { useContext, useEffect, useState } from 'react'
import { Controller, type SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { supportRequest } from './Support.service'

interface SupportRequestDialogPayload {
  weblinkToken?: string
  starRating?: number
}

export interface SupportRequestDialogProps {
  title?: string
  description?: string
  payload: SupportRequestDialogPayload
  open: boolean
  onAction: () => void
  onClose: () => void
}

export interface SupportRequestFormValues {
  language: string
  url: string
  customerType: CompanyType
  requestType: 'Feedback' | 'Issue' | 'Question' | 'UserSatisfaction'
  email: string
  title: string
  message: string
  starRating: number
}

const SupportRequestDialog: React.FC<SupportRequestDialogProps> = ({
  title,
  description,
  payload,
  open,
  onAction,
  onClose,
}) => {
  const { t } = useTranslation()
  const { handleSubmit, reset, control, formState, setError } = useForm<SupportRequestFormValues>()
  const [showConfirmationSnackbar, setShowConfirmationSnackbar] = useState(false)
  const { user } = useContext(UserContext)
  const supportRequestMutation = useMutation({
    mutationFn: (data: SupportRequestFormValues) => supportRequest(data, payload.weblinkToken),
    onError: (error) => setError('root', processServerErrorMessages(error)),
  })

  useEffect(() => {
    if (open) {
      reset(
        processDefaultValues({
          language: i18n.language.slice(0, 2),
          url: window.location.href,
          customerType: CompanyType.Carrier,
          email: user?.email ?? null,
          title: null,
          message: null,
          starRating: null,
        }),
      )
    }
  }, [open])

  const onSubmit: SubmitHandler<SupportRequestFormValues> = async (data: SupportRequestFormValues) => {
    await supportRequestMutation.mutateAsync(data)
    onAction()
    setShowConfirmationSnackbar(true)
    analyticsEvent('polestar_cs_support_message_sent', [data.requestType])
  }

  const handleSnackbarClose = (): void => setShowConfirmationSnackbar(false)

  return (
    <>
      <KNDialog
        open={open}
        onClose={onClose}
        closeLabel={t('general.cancel')}
        preventClosing={formState.isSubmitting}
        title={t('shared.support.feedback_support')}
        actions={
          <Button
            color="secondary"
            variant="contained"
            loading={formState.isSubmitting}
            onClick={handleSubmit(onSubmit)}
          >
            {t('form.send')}
          </Button>
        }
        onSubmit={handleSubmit(onSubmit)}
      >
        <DialogContentText component="div" mb={3}>
          <Typography color="text.main">{description ?? t('shared.support.notice')}</Typography>
          <Typography color="text.main">{t('shared.support.language_notice')}</Typography>
        </DialogContentText>
        <KNDialogFormErrors errors={formState.errors?.root} />
        <Stack spacing={2}>
          <KNFormButtonGroup
            name="requestType"
            control={control}
            rules={{
              required: t('form.validation.required'),
            }}
            options={['Feedback', 'Issue', 'Question'].map((type) => ({
              value: type,
              label: t(`shared.support.types.${type}`),
            }))}
          />
          {payload?.weblinkToken && (
            <KNFormText
              name="email"
              placeholder={t('shared.support.email')}
              control={control}
              rules={{
                pattern: {
                  value: regexEmail,
                  message: t('form.validation.invalid_email'),
                },
                required: t('form.validation.required'),
              }}
            />
          )}
          {!title && (
            <KNFormText
              name="title"
              placeholder={t('shared.support.title')}
              control={control}
              rules={{
                required: t('form.validation.required'),
              }}
            />
          )}
          <KNFormText
            name="message"
            placeholder={t('shared.support.comment')}
            control={control}
            rows={2}
            rules={{
              required: t('form.validation.required'),
            }}
          />
          <Typography variant="p1">{description ?? t('shared.support.star_rating.like_rcs')}</Typography>
          <Controller
            name="starRating"
            control={control}
            render={({ field }) => (
              <Rating
                name="starRating"
                value={field.value}
                size="large"
                emptyIcon={<StarBorderIcon fontSize="inherit" color="warning" />}
                onChange={(_, newValue): void => field.onChange(newValue)}
              />
            )}
          />
        </Stack>
      </KNDialog>

      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={showConfirmationSnackbar}
        onClose={handleSnackbarClose}
        key="supportRequestConfirmation"
      >
        <Alert variant="filled" severity="success">
          {t('shared.support.confirmation')}
        </Alert>
      </Snackbar>
    </>
  )
}

export default SupportRequestDialog
